import cookies from 'js-cookie';
import platform from 'platform';
import { debounce } from 'lodash-es';
import { AB_TESTING_NON_CACHEABLE_PAGES } from '../utils/constants';

const EVENTS_PREFIXES = {
  index: 'home',
  'courses-course': 'courseslanding',
  'career-tracks-track': 'career_track_landing',
  'blogCategory-blogSubItem': 'blogpost',
  'blogCategory-blogSubItem-blogArticle': 'blogpost',
  'resources-center-category-slug': 'resourceslanding',
};

let TRACKING_SCROLL_SECTIONS = [];

function getElementViewportInfo(el) {
  const rect = el.getBoundingClientRect();
  const windowHeight = (window.innerHeight || document.documentElement.clientHeight);

  const vertInView = (rect.top <= windowHeight / 3) && ((rect.top + rect.height) >= 0);

  return { vertInView, rect };
}

const asyncEvents = { };

export default async ({
  $axios, $config, route, store, req,
}, inject) => {
  const tracking = {
    async startSession() {
      if (store.state.tracking.sessionId) {
        store.dispatch('tests/setUserTests', store.state.tests.tests);

        if (store.state.tracking.visitorId) {
          this.setVisitorId(store.state.tracking.visitorId);
        }

        return store.state.tracking.sessionId;
      }

      if (route.query.state) {
        const state = route.query.state.split(',').map((s) => s.split('=')).reduce((p, [k, v]) => ({ ...p, [k]: v }), { });

        if (state.sessionId) {
          store.commit('tracking/setSessionId', state.sessionId);

          if (state.visitorId) {
            this.setVisitorId(state.visitorId);
          }

          if (state.event && state.sourceUrl && state.destinationUrl && route.query.error) {
            const payload = {
              event: `${state.event}.fail`,
              sourceUrl: decodeURIComponent(state.sourceUrl),
              destinationUrl: decodeURIComponent(state.destinationUrl),
              normalized: true,
              error: route.query.error,
            };

            this.sendEvent(payload);
          }

          return false;
        }
      }

      let platformInfo = platform;

      if (req && req.headers['user-agent']) {
        platformInfo = platform.parse(req.headers['user-agent']);
      }

      const { sessionId, visitorId, tests } = await $axios.$post('/tracking/session', {
        visitorId: this.getVisitorId(),
        utmMedium: route.query.utm_medium,
        utmSource: route.query.utm_source,
        utmContent: route.query.utm_content,
        utmCampaign: route.query.utm_campaign,
        path: route.fullPath,
        browser: `${platformInfo.name} ${platformInfo.version}`,
        device: `${platformInfo.os.toString()}`,
      }, { noError: true });

      store.dispatch('tests/setUserTests', tests);

      if (process.server) {
        // res.setHeader('Set-Cookie', `abTests=${JSON.stringify(tests)}; path=/; expires=${new Date(new Date().setDate(new Date().getDate() + 1000))}; domain=.${$config.primaryCookiesDomain}`);
      }

      if (process.client) {
        this.setVisitorId(visitorId);
      }

      cookies.remove('session_id', { domain: '365datascience.com' });
      cookies.remove('visitor_id', { domain: '365datascience.com' });

      store.commit('tracking/setSessionId', sessionId);
      store.commit('tracking/setVisitorId', visitorId);

      return sessionId;
    },
    setVisitorId(id) {
      if (!this.getVisitorId()) {
        cookies.set('_visitor_id', id, { expires: 1000, domain: `.${$config.primaryCookiesDomain}` });
      }
    },
    getVisitorId() {
      return cookies.get('_visitor_id');
    },
    getFbc() {
      return cookies.get('_fbc'); // this will work only on production beacuse of 365datascience.com domain
    },
    getFbp() {
      return cookies.get('_fbp'); // this will work only on production beacuse of 365datascience.com domain
    },
    getAsyncEvents() {
      return asyncEvents;
    },
    async sendAsyncEvent(event, payload) {
      const e = asyncEvents[event];

      if (e) {
        await e.send(payload);
      }

      return false;
    },
    setTrackingSections() {
      TRACKING_SCROLL_SECTIONS = document.querySelectorAll('.tracking-section');
    },
    resetTrackingSections() {
      TRACKING_SCROLL_SECTIONS = [];
    },
    setEventListeners() {
      window.addEventListener('DOMContentLoaded', () => {
        this.setTrackingSections();
      });

      window.addEventListener('click', async (e) => {
        let { target } = e;
        let { event } = target.dataset;

        if (!event && e.target.closest('[data-event$=".click"]')) {
          ({ event } = e.target.closest('[data-event$=".click"]').dataset);
          target = e.target.closest('[data-event$=".click"]');
        }

        if (event) {
          store.commit('tracking/setEvent', event);

          if (target.getAttribute('target') || target.hasAttribute('data-direct-send')) {
            if (target.hasAttribute('data-async-event')) {
              const payload = {
                event,
                sourceUrl: window.$nuxt.$route.fullPath,
                destinationUrl: window.$nuxt.$route.fullPath,
                route: window.$nuxt.$route,
              };

              asyncEvents[event] = {
                send: async ({ status, error }) => this.sendEvent({
                  ...payload,
                  error,
                  event: status ? `${event}.${status}` : event,
                }),
                payload: this.normalizeEventPayload(payload),
              };
            } else {
              await this.sendEvent({
                event,
                sourceUrl: window.$nuxt.$route.fullPath,
                destinationUrl: window.$nuxt.$route.fullPath,
                route: window.$nuxt.$route,
              });
            }

            store.commit('tracking/setEvent', null);
          }
        }
      }, true);

      window.addEventListener('scroll', debounce(() => this.sectionIsVisible((e) => {
        if (store.state.tracking.event !== e) {
          store.commit('tracking/setEvent', e);
          this.sendEvent({
            event: e,
            sourceUrl: window.$nuxt.$route.fullPath,
            destinationUrl: window.$nuxt.$route.fullPath,
            route: window.$nuxt.$route,
          });
        }
      }), 1000));

      window.popStateDetected = false;
      window.addEventListener('popstate', () => {
        window.popStateDetected = true;
      });
    },
    sectionIsVisible(cb) {
      const visibleSections = [];
      TRACKING_SCROLL_SECTIONS.forEach((s) => {
        const viewportInfo = getElementViewportInfo(s);
        if (viewportInfo.vertInView) {
          const { event } = s.dataset;

          if (event) {
            visibleSections.push({ top: viewportInfo.rect.top, event });
          }
        }
      });

      const [section] = visibleSections.sort((a, b) => b.top - a.top) || [];

      if (section && section.event) {
        cb(section.event);
      }
    },
    normalizeEventPayload(e) {
      if (e.normalized) {
        return e;
      }

      let prefix = '';
      if (e.route) {
        prefix = EVENTS_PREFIXES[e.route.name] || e.route.name.replace('-', '_');
      }

      const event = {
        event: prefix && e.event ? `${prefix}_${e.event}` : '',
        dataLayerEvent: e.dataLayerEvent || 'PageView',
        destinationUrl: e.destinationUrl,
        sourceUrl: e.sourceUrl,
        error: e.error,
        fbp: this.getFbp(),
        fbc: this.getFbc(),
        value: e.value,
        currency: e.currency,
        eventID: e.eventID,
        userAgent: e.userAgent,
        normalized: true,
      };

      return event;
    },
    async sendEvent(e) {
      const event = this.normalizeEventPayload(e);

      const { sessionId } = await $axios.$post('/tracking/session/events', {
        events: [event],
        sessionId: store.state.tracking.sessionId,
        utmMedium: route.query.utm_medium,
        utmSource: route.query.utm_source,
        utmContent: route.query.utm_content,
        utmCampaign: route.query.utm_campaign,
        browser: `${platform.name} ${platform.version}`,
        device: `${platform.os.toString()}`,
        capi: e.capi,
        session: e.session,
      }, { progress: false, noError: true });

      if (store.state.tracking.sessionId !== sessionId) {
        store.commit('tracking/setSessionId', sessionId);
      }

      return sessionId;
    },
  };

  inject('tracking', tracking);

  if (AB_TESTING_NON_CACHEABLE_PAGES.includes(route.path) && process.server) {
    await tracking.startSession();
  } else if (process.client) {
    tracking.setEventListeners();
    const sessionId = await tracking.startSession();
    dataLayer.push({
      event: 'Pageview',
      pagePath: window.location.pathname + window.location.search,
      pageTitle: document.title,
      eventID: sessionId,
    });
  }
};
